import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { ApiError } from '../services/apiClient';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Missing reset token. Please use the link from your email.');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setError('');
    setLoading(true);

    try {
      await authService.resetPassword(token, newPassword, confirmPassword);
      setSuccess(true);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.data) {
        const d = err.data as Record<string, unknown>;
        const tokenErr = Array.isArray(d.token) ? d.token[0] : d.token;
        const newPw = Array.isArray(d.new_password) ? d.new_password[0] : d.new_password;
        const confirmPw = Array.isArray(d.confirm_password) ? d.confirm_password[0] : d.confirm_password;
        setError((tokenErr || newPw || confirmPw || err.message) as string);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to reset password');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-surface rounded-2xl shadow-xl border border-border p-8 text-center">
            <div className="mx-auto h-16 w-16 bg-success/10 rounded-full flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-fg mb-2">Password reset</h1>
            <p className="text-fg-secondary mb-6">Your password has been updated. You can now log in.</p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-xl text-sm font-medium text-white bg-primary hover:bg-primary-dark"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-primary rounded-2xl flex items-center justify-center mb-4">
            <span className="text-2xl font-bold text-white">LP</span>
          </div>
          <h1 className="text-3xl font-bold text-primary">
            Set new password
          </h1>
          <p className="mt-2 text-fg-secondary">Enter your new password below.</p>
        </div>

        <div className="bg-surface rounded-2xl shadow-xl border border-border p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="new_password" className="block text-sm font-medium text-fg mb-2">
                New Password
              </label>
              <input
                id="new_password"
                name="new_password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                className="input-field py-3 rounded-xl"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={!token}
              />
              <p className="mt-1 text-xs text-fg-muted">Must be at least 8 characters.</p>
            </div>

            <div>
              <label htmlFor="confirm_password" className="block text-sm font-medium text-fg mb-2">
                Confirm Password
              </label>
              <input
                id="confirm_password"
                name="confirm_password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                className="input-field py-3 rounded-xl"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={!token}
              />
            </div>

            {error && (
              <div className="alert-danger rounded-xl p-4">
                <p className="text-sm text-danger-fg">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !token}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-app focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Resetting...
                </>
              ) : (
                'Reset password'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm font-medium text-primary hover:text-primary-dark">
              Back to login
            </Link>
            {!token && (
              <span className="mx-1">|</span>
            )}
            {!token && (
              <Link to="/forgot-password" className="text-sm font-medium text-primary hover:text-primary-dark">
                Request new link
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
